import React, { useRef } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from 'antd';
import { PlusOutlined, PaperClipOutlined } from '@ant-design/icons';

import { GET_ANNUAL_REPORTS } from 'gql/annualReports';
import { ProTable, Link, DateDisplay } from 'components/layout';
import {
  renderProTableSearchFaculty,
  renderProTableSearchProjectCategory,
} from 'components/layout/ProTable';
import { stringSorter } from 'utils';
import { useHasScopeType } from 'hooks';
import { GET_MY_CURRENT_USER_ROLE } from 'gql/auth';
import { PageContainer } from '@ant-design/pro-layout';
import { PrivateComponent } from 'components/auth';
import { SCOPE_TYPES } from 'constants/scopes';
import { EditButton } from 'components/button';
import DeleteAnnualReportButton from './components/DeleteAnnualReportButton';

const downloadUrl = process.env.REACT_APP_DOWNLOAD_URL;

const Reports = () => {
  const ref = useRef();
  const { data, loading: loadingMe } = useQuery(GET_MY_CURRENT_USER_ROLE);
  const { hasScopeType, loading } = useHasScopeType(
    SCOPE_TYPES.VIEW_ALL_ANNUAL_REPORT
  );
  const { hasScopeType: hasEditScopeType } = useHasScopeType(
    SCOPE_TYPES.WRITE_ANNUAL_REPORT_WITH_CUSTOM_DATE
  );
  const { refetch } = useQuery(GET_ANNUAL_REPORTS, {
    variables: {
      faculty: hasScopeType ? null : data?.myCurrentUserRole?.department?.id,
    },
  });

  const columns = [
    {
      title: 'Faculty',
      key: 'faculty',
      render: ({ faculty, id }) => (
        <Link to={`/annual-reports/${id}`}>{faculty.abbreviation}</Link>
      ),
      renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
        renderProTableSearchFaculty(
          _,
          { type, defaultValue, disabled: !hasScopeType, ...rest },
          form
        ),
      initialValue: hasScopeType
        ? undefined
        : data?.myCurrentUserRole?.faculty?.id,
    },
    {
      title: 'Project Category',
      key: 'projectCategory',
      sorter: (a, b) =>
        stringSorter(a.projectCategory.name, b.projectCategory.name),
      render: ({ projectCategory, id }) => (
        <Link to={`/annual-reports/${id}`}>{projectCategory.name}</Link>
      ),
      renderFormItem: renderProTableSearchProjectCategory,
    },
    {
      title: 'Reporting Period',
      key: 'reportingPeriod',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(a.reportingPeriod?.id, b.reportingPeriod?.id),
      render: ({ id, reportingPeriod }) => (
        <Link to={`/annual-reports/${id}`}>
          <DateDisplay date={reportingPeriod?.startDate} /> -{' '}
          <DateDisplay date={reportingPeriod?.endDate} />
        </Link>
      ),
    },
    {
      title: 'Scheduled Deadline',
      hideInSearch: true,
      dataIndex: ['reportingPeriod', 'scheduledDeadline'],
      render: (_, { id }) => (
        <Link to={`/annual-reports/${id}`}>
          <DateDisplay date={_} />
        </Link>
      ),
    },
    {
      title: 'Actual Submission Date',
      hideInSearch: true,
      dataIndex: 'actualSubmissionDate',
      render: (_, { id }) => (
        <Link to={`/annual-reports/${id}`}>
          <DateDisplay date={_} />
        </Link>
      ),
    },
    {
      title: 'Attachment',
      key: 'attachment',
      hideInSearch: true,
      render: ({ attachment }) => (
        <Button type="primary" href={`${downloadUrl}${attachment.id}`}>
          <PaperClipOutlined /> Download
        </Button>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      hideInTable: !hasEditScopeType,
      hideInSearch: true,
      render: (_, item) => (
        <>
          <Link to={`/annual-reports/${item.id}/edit`}>
            <EditButton />
          </Link>
          <DeleteAnnualReportButton id={item.id} actionRef={ref} />
        </>
      ),
    },
  ];

  return (
    <PageContainer loading={loadingMe || loading}>
      <ProTable
        actionRef={ref}
        scroll={{ x: 1080 }}
        headerTitle="Annual Reporting Submission Records"
        columns={columns}
        request={async (params = {}, _sort, _filter) => {
          try {
            const { faculty, projectCategory } = params;
            const result = await refetch({
              // departments: department ? [department] : null,
              faculty: faculty ? faculty : null,
              projectCategory: projectCategory ? projectCategory : null,
            });
            return {
              data: result.data?.annualReports,
              success: true,
            };
          } catch (error) {
            console.log(error);
            return {
              data: [],
              success: false,
            };
          }
        }}
        options={{
          density: false,
        }}
        search={{
          defaultCollapsed: false,
          searchText: 'Search',
          span: {
            xs: 24,
            sm: 24,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
          },
          labelWidth: 'auto',
        }}
        pagination={{ pageSize: 20 }}
        rowKey="id"
        toolBarRender={() => [
          <PrivateComponent
            key="add"
            expectedScopeType={[
              SCOPE_TYPES.WRITE_ANNUAL_REPORT,
              SCOPE_TYPES.WRITE_ANNUAL_REPORT_WITH_CUSTOM_DATE,
            ]}
          >
            <Link to="/annual-reports/add">
              <Button key="button" icon={<PlusOutlined />} type="primary">
                Submit Annual Report
              </Button>
            </Link>
          </PrivateComponent>,
        ]}
      />
    </PageContainer>
  );
};

export default Reports;
